import {Injector, Injectable, runInInjectionContext} from '@angular/core'
import {defaultSurveyConstructorParams, Survey} from 'app/models/Survey'
import {IHaveRules, ISurvey} from 'app/interfaces/ISurvey'
import {UserService} from 'app/core/user/user.service'
import {AngularFireDatabase} from '@angular/fire/compat/database'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take
} from 'rxjs'
import {ToastrService} from 'ngx-toastr'
import { HttpClient } from '@angular/common/http'
import {environment} from 'environments/environment'
import {PublicationAccessService} from 'app/modules/landing-publication/publication-access.service'
import {UrlEntryService} from 'app/core/urlEntry/url-entry.service'
import {TranslocoService} from '@ngneat/transloco'
import {AuthService} from 'app/core/auth/auth.service'
import {Router} from '@angular/router'
import {UserError} from 'app/custom-errors/UserError'
import {Question} from 'app/models/questions/Question'
import {requireMinRowSelection} from 'app/form-validators/RequireMinRowSelectionValidator'
import {requireMinStatementSelection} from 'app/form-validators/RequireMinStatementSelectionValidator'
import {requireSelectionOnEachMatrixRow} from 'app/form-validators/RequireSelectionOnEachMatrixRowValidator'
import {RewardService} from "../../../reward/reward.service"
import {Matrix} from 'app/models/questions/Matrix'
import {EventMessagesService, eventType} from "@store/event-messages.service"
import {requireMinImageSelection} from 'app/form-validators/RequireMinImageSelectionValidator'
import Bugsnag from '@bugsnag/js'
import {AppModeService} from '@store/services/app-mode.service'
import {AppMode, AppPreviewMode} from 'app/enums/AppModeEnum'
import {EditResponseService} from "../../survey-response-edit/edit-response.service";
import {AbstractControl, AsyncValidatorFn, ValidationErrors} from "@angular/forms";
import {HoneyPotService} from "../survey-question/types/honey-pot/honey-pot.service";
import {cloneDeep} from "lodash-es";
import {TestSurveyService} from "../../../../../@fuse/components/test-survey/test-survey.service";
import {FlowFeedbackService} from "@store/flow-feedback/flow-feedback.service";
import {LangsListParamsService} from "@store/langs-list-params.service";
import {InputPersistenceService} from "./input-persistence.service";
import {AudioUploadService} from "@store/file-upload/audio-upload.service";
import {ViewerTypeEnum} from 'app/enums/ViewerTypeEnum';

@Injectable({
  providedIn: 'root'
})
export class SurveyViewerService {

  errorMessages = {
    minImageSelection: (required: number) => {
      const imageWrd = required > 1 ? 'images' : 'image'
      return {
        fr: `Sélectionnez au moins ${required} ${imageWrd}.`,
        en: `Choose at least ${required} ${imageWrd}.`
      }
    },
    minCheckboxSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix.`,
        en: `Select at least ${required} ${choiceWrd}.`
      }
    },
    minRowSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix par ligne.`,
        en: `Select at least ${required} ${choiceWrd} per line.`
      }
    },
    minStatementSelection: (required: number) => {
      const choiceWrd = required > 1 ? 'choices' : 'choice'
      return {
        fr: `Sélectionnez au moins ${required} choix par colonne.`,
        en: `Select at least ${required} ${choiceWrd} per column.`
      }
    }
  }

  alreadyElapsedTime: number;
  allFormControlsMarkedAsTouched$ = new BehaviorSubject<boolean>(false)
  isLoadingSurveyMeta$ = new BehaviorSubject<boolean>(false)
  hasPendingRequest$ = new BehaviorSubject<boolean>(false)
  activeSurvey$ = new BehaviorSubject<Survey>(null)
  haveRules$: BehaviorSubject<IHaveRules> = new BehaviorSubject<IHaveRules>(null);
  pendingAudioValidations$ = new BehaviorSubject<string[]>([]);
  invalidAudioQuestions$ = new BehaviorSubject<string[]>([]);

  initialRawSurvey: ISurvey
  initialRawSurvey$ = new BehaviorSubject<ISurvey>(null)

  maxFillingRetries = 2000
  requireMinImageSelectionFnName = requireMinImageSelection.name
  minRowErrorFnName = requireMinRowSelection.name
  minStatementErrorFnName = requireMinStatementSelection.name
  requireSelectionForEachStatementFnName = requireSelectionOnEachMatrixRow.name
  defaultImageUrl = ''
  private _createUserOnSubmit: boolean;


  private formSub$: Subscription
  private answers$ = new BehaviorSubject<{}>(null)

  constructor(
    private db: AngularFireDatabase,
    private userService: UserService,
    private toastr: ToastrService,
    private http: HttpClient,
    private publicationAccessService: PublicationAccessService,
    private urlEntryService: UrlEntryService,
    private translocoService: TranslocoService,
    private authService: AuthService,
    private rewardService: RewardService,
    private router: Router,
    private eventMessagesService: EventMessagesService,
    private appModeService: AppModeService,
    private editResponseService: EditResponseService,
    private honeyPotService: HoneyPotService,
    private testSurveyService: TestSurveyService,
    private flowFeedbackService: FlowFeedbackService,
    private langsListParamsService: LangsListParamsService,
    private inputPersistence: InputPersistenceService,
    private injector: Injector,
    private audioUploadService: AudioUploadService,
  ) {

    combineLatest(
      [
        this.appModeService.isTestSurvey$,
        this.userService.user$,
        this.initialRawSurvey$
      ]
    )
      .pipe(
        distinctUntilChanged(),
        filter(([isTestSurvey, user, initialRawSurvey]) => isTestSurvey === true && !!user && !!initialRawSurvey)
      )
      .subscribe(([isTestSurvey, user, survey]) => {
        if (this.activeSurvey$.value) {
          if (this.appModeService.isTestSurvey$.value) {
            const toSave = {
              timeStamp: new Date().toISOString(),
              datas: this.buildDataPayload(),
            }
            this.testSurveyService.updateValuesForTest(this.haveRules$.value, toSave);
          }
          this.relaunchEvalRules();
        }
      });

  }

  changeFormUpdateStrategy(formControl: AbstractControl, strategy: 'blur' | 'change') {
    Object.defineProperty(formControl, 'updateOn', {
      get: () => strategy,
      configurable: true
    });
  }

  changeCreateUserOnSubmit(value: boolean) {
    this._createUserOnSubmit = value;
  }

  getRowLabelImageUrl(question: Matrix, rowKey: string, activeLang: string) {
    if (!question.rows[rowKey].media) {
      return this.defaultImageUrl
    }

    if (!question.rows[rowKey].media[activeLang]) {
      const availableLanguages = Object.keys(question.rows[rowKey].media)
      return question.rows[rowKey].media[availableLanguages.find(el => el)]['url']
    }

    return question.rows[rowKey].media[activeLang]['url']
  }

  getStatementLabelImageUrl(question: Question, statementKey: string, activeLang: string): string {
    if (!question.statements[statementKey]?.media) {
      return this.defaultImageUrl
    }

    if (!question.statements[statementKey].media[activeLang]) {
      const availableLanguages = Object.keys(question.statements[statementKey].media)
      return question.statements[statementKey].media[availableLanguages.find(el => el)]['url']
    }

    return question.statements[statementKey].media[activeLang]['url']
  }

  fillAnswers(): void {
    this.resetSurveyForm()
    try {
      this.activeSurvey$.value.getQuestionsReferencedInStructureElements().forEach(q => {
        q.isOptional ?
          this.fillRandomly(q) :
          q.fillFormWithDummyAnswer(this.activeSurvey$.value.form, 0, this.maxFillingRetries)
      })
      this.activeSurvey$.value.form.updateValueAndValidity()
    } catch (e) {
      Bugsnag.notify(e, (event) => {
        event.addMetadata('Survey Viewer Service', {
          surveyDef: this.activeSurvey$.value.surveyDef,
          surveyId: this.activeSurvey$.value.id
        })
      })
      console.error(e.message)
      console.error(e.stack)
      this.toastr.error(e, this.translocoService.translate('Erreur de remplissage du sondage'), {
        disableTimeOut: true
      })
    }
  }

  static allowRandomly() {
    return Math.random() <= 0.8
  }

  private fillRandomly(q: Question) {
    if (SurveyViewerService.allowRandomly()) {
      q.fillFormWithDummyAnswer(this.activeSurvey$.value.form, 0, this.maxFillingRetries)
    }
  }

  resetSurveyForm(): void {
    this.answers$.next(null)
    this.activeSurvey$.value.form.reset()
    this.initializeViewer(this.initialRawSurvey)
  }

  getSurvey(surveyId: number) {

    this.flowFeedbackService.surveyIsLoading(true);

    //
    // Mode Reply = Get Survey from Database
    //
    if (this.appModeService.appMode$.value === AppMode.REPLY || this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
      // if (this.appModeService.appMode$.value === AppMode.REPLY) {

      const endpoint = `${environment.apiUrl}/4dm1n/publicActions`
      this.isLoadingSurveyMeta$.next(true)

      const actionName = 'getSurvey'
      const body = {
        action: actionName,
        params: {
          surveyId,
          userId: this.userService.user.id
        }
      }

      this.http
        .post(endpoint, body)
        .subscribe({
          next: (response: {
            data: {
              datas: ISurvey
            }
          }) => {
            if (!response?.data?.datas || !Object.keys(response.data.datas).length) {
              this.toastr.error(this.translocoService.translate(`Impossible de récupérer le sondage à l'adresse`) + ` ${endpoint}`, this.translocoService.translate('Sondage non trouvé'), {
                disableTimeOut: true
              })
              this.isLoadingSurveyMeta$.next(false)
              return
            }

            if (response.data.datas.alreadyAnsweredByUser && this.appModeService.appMode$.value !== AppMode.EDIT_RESPONSE) {
              this.router.navigate(['/home']).then(() => {
                this.isLoadingSurveyMeta$.next(false)
              })
              this.flowFeedbackService.surveyIsLoading(false);
              return
            }

            const survey = response.data.datas
            this.initialRawSurvey = survey

            this.initializeViewer(survey)

            if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
              this.patchResponses(this.editResponseService.rawResultFromDatabase);
            } else {
              this.getAnswers(surveyId)
            }

            this.isLoadingSurveyMeta$.next(false)
            this.flowFeedbackService.surveyIsLoading(false);

          }, error: err => {
            this.isLoadingSurveyMeta$.next(false)
            Bugsnag.notify(err, (event) => {
              event.addMetadata('Survey Viewer Service', {
                method: this.getSurvey.name,
                body,
                endpoint
              })
            })
          }
        })

      return
    }

    //
    // Mode Preview = Get Survey from Firebase
    //

    const surveyUrl = `/surveys/${surveyId}`

    runInInjectionContext(this.injector, () => {
      this.db.object(surveyUrl)
        .valueChanges()
        .subscribe({
          next: (response: ISurvey) => {
            runInInjectionContext(this.injector, () => {
              if (!response) {
                this.toastr.error(this.translocoService.translate(`Impossible de récupérer le sondage à l'adresse`) + ` ${surveyUrl}`, this.translocoService.translate('Sondage non trouvé'), {
                  disableTimeOut: true
                })
                Bugsnag.notify(new Error('No survey available at the specified address'), (event) => {
                  event.addMetadata('Survey Viewer Service', {
                    endpoint: surveyUrl,
                    surveyId,
                    appMode: this.appModeService.appMode$.value
                  })
                })
                return
              }

              this.initialRawSurvey = response
              if (!this.initialRawSurvey.surveyDef.questions){
                this.initialRawSurvey.surveyDef.questions = this.initialRawSurvey.questions
              }

              this.flowFeedbackService.setInitializeViewer(true);
              this.initializeViewer(response)

              //
              // 2 Cas : si on est en mode preview on va aller chercher le Raw Result dans la BD
              //         sinon ce sera celui de Firebase
              //
              if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {
                this.patchResponses(this.editResponseService.rawResultFromDatabase);
              } else {
                this.getAnswers(surveyId)
              }
              //
              // this.getAnswers(surveyId)
              //
              //
              //

              this.flowFeedbackService.setInitializeViewer(false);
              this.flowFeedbackService.surveyIsLoading(false);
            })
          }, error: err => {
            runInInjectionContext(this.injector, () => {
              Bugsnag.notify(err, (event) => {
                event.addMetadata('Survey Viewer Service', {
                  method: this.getSurvey.name,
                  endpoint: surveyUrl
                })
              })
            })
          }
        })
    })
  }

  userExistValidator(createUser: boolean): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => !createUser ? null :
      this.http.post(`${environment.apiUrl}/appCommunity/checkForEmail`,
        {action: 'checkForEmail', params: {email: control.value, surveyId: this.activeSurvey$.value.id}})
        .pipe(map((res) => res['data'] ? {userExist: {value: control.value}} : null));
  }

  submitSurvey() {
    if (this.hasPendingRequest$.value) {
      return false
    }

    if (!this.userService.user) {
      this.toastr.error(
        this.translocoService.translate('Aucun utilisateur en cours ?'),
        this.translocoService.translate(`Impossible d'enregistrer les réponses.`)
      )
      this.hasPendingRequest$.next(false)
      Bugsnag.notify(new UserError().isMissing())
      throw new UserError().isMissing()
    }

    if (this.appModeService.appMode$.value === AppMode.PREVIEW) {
      this.notifySuccessForPreviewMode()
      return false
    }

    // Vérifier s'il y a des uploads en cours
    if (this.audioUploadService.hasAnyUploadInProgress()) {
      const questionsWithUpload = this.audioUploadService.getQuestionsWithUploadInProgress()
      this.pendingAudioValidations$.next(questionsWithUpload)
      this.toastr.warning(
        this.translocoService.translate('Veuillez attendre la fin de tous les téléchargements audio avant de soumettre le sondage.'),
        this.translocoService.translate('Téléchargement audio en cours'),
        { disableTimeOut: true }
      )
      return false
    }

    // Vérifier si toutes les questions audio sont validées complètement
    const pendingAudioQuestions = this.getPendingAudioValidations()
    if (pendingAudioQuestions.length > 0) {
      this.pendingAudioValidations$.next(pendingAudioQuestions)
      this.toastr.warning(
        this.translocoService.translate('Veuillez attendre la validation complète de toutes les questions audio avant de soumettre le sondage.'),
        this.translocoService.translate('Validation audio en cours'),
        { disableTimeOut: true }
      )
      return false
    }

    //
    // Penser à sauvegarder une ultime fois dans firebasse
    //
    this.syncAnswersWithFirebase().then(() => {

      // Nettoyer toutes les valeurs temporaires sauvegardées lors de la soumission
      this.inputPersistence.clearAll();

      this.hasPendingRequest$.next(true)

      const endpoint = `${environment.apiUrl}/appCommunity/submitResponses`;
      const body = {
        ...this.buildSurveySubmitBodyPayload(),
        editResponse: this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE,
        createUser: {
          status: this._createUserOnSubmit,
          commId: this.userService.user.publicStatus?.rid ? null : this.activeSurvey$.value.originalSurveyDef.specialInfo?.communityForNewUser
        },
      }

      this.http.post(endpoint, body).subscribe({

        next: (response: any) => {
          // Appel à la nouvelle lambda de validation AI
          this.callValidateAnswerWithAI(body, response);

          if (this.appModeService.appMode$.value === AppMode.EDIT_RESPONSE) {

            this.toastr.success(
              this.translocoService.translate('RESPONSE UPDATED'),
              '[EDIT RESPONSES]'
            )
            this.hasPendingRequest$.next(false)
            return false;

          } else {

            const userDetails = response.data.userDetails

            this.eventMessagesService.setSurveyId(parseInt(this.activeSurvey$.value.id))
            this.eventMessagesService.emitEvent(eventType.surveyReplied, {})

            this.toastr.success(
              this.translocoService.translate('Vos réponses ont bien été enregistrées.'),
              this.translocoService.translate('Réponses soumises.')
            )

            this.authService.accessToken = userDetails.user.jwtToken
            if (this.userService.user.publicStatus?.rid) {
              window.location.replace(`https://notch.insights.supply/cb?token=4ce73598-6410-4d10-b788-349fa171ebd8&RID=${this.userService.user.publicStatus?.rid}`)
            }
            this.updateUserExtProfiles(userDetails)

          }
        },

        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Survey Viewer Service', {
              method: this.submitSurvey.name,
              payload: body,
              endpoint,
              errorMsg: err.error.message,
              jwtToken: this.authService.accessToken
            })
          })
          this.toastr.error(err.error.message, this.translocoService.translate(`Les réponses n'ont pu être enregistrées`), {
            disableTimeOut: true
          })
        }
      })


    });
  }

  private buildSurveySubmitBodyPayload() {
    return {
      surveyId: this.activeSurvey$.value.id,
      datas: this.buildDataPayload(true),
      isPublic: this.userService.user.isPublic,
      lang: this.translocoService.getActiveLang(),
      publicStatus: {
        ...this.userService.user.publicStatus,
        ext: this.userService.extractExtProfilesForPublicUser() || {},
      },
      finalSurveyDef: {},
      extProfileQuestions: this.extractExtProfileQuestions(),
      pubUuid: this.publicationAccessService?.pubUuid,

    }
  }

  private extractExtProfileQuestions() {
    const profileQuestions = []
    this.activeSurvey$.value.pages.forEach(p => {
      p.questions.forEach(q => {
        if (q.shouldSaveInExtProfile) {
          profileQuestions.push({
            keyName: q.key,
            keyValue: JSON.stringify(this.activeSurvey$.value.form.get(q.key).value)
          })
        }
      })
    })
    return profileQuestions
  }

  private updateUserExtProfiles(userDetails) {
    const endpoint = `${environment.apiUrl}/user/extprofils`
    return this.http.post(endpoint, {
      userId: userDetails.user.id,
      list: []
    }).subscribe({
      next: (response: any) => {
        const extProfileMap = {}
        response.data.exts.rows.forEach(r => extProfileMap[r.key_name] = r.key_value)
        this.userService.user$.next({...this.userService.user$.value, extProfiles: extProfileMap})
        this.relogUserAfterSubmitSurvey(userDetails.user.jwtToken)
        this.hasPendingRequest$.next(false)
      },
      error: err => {
        this.hasPendingRequest$.next(false)
        Bugsnag.notify(err, (event) => {
          event.addMetadata('Survey Viewer Service', {
            endpoint,
            user: userDetails,
            methodCalled: this.updateUserExtProfiles.name
          })
        })
      }
    })
  }

  private getAnswers(surveyId: number): void | false {
    if (this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION) {
      return false
    }

    const answerUrl = `temp/${surveyId}/${this.userService.user.id}`
    runInInjectionContext(this.injector, () => {
      this.db.object(answerUrl)
        .valueChanges()
        .subscribe((response: {
          datas?: any,
          timeStamp: string
        }) => {
          runInInjectionContext(this.injector, () => {
            if (!response || !response.datas) {
              return
            }
            this.alreadyElapsedTime ??= response.datas?.elapsedDetail?.elapsedInSeconds || 0;
            this.patchResponses(response.datas);
          })
        })
    })
  }

  public patchResponses(datas) {

    this.answers$.next(datas)
    this.activeSurvey$.value.applyRules(datas)
    this.activeSurvey$.value.form.patchValue(datas, {emitEvent: false})
    this.activeSurvey$.next(this.activeSurvey$.value)
    this.applyMaxAnswers()

  }

  public relaunchEvalRules() {
    // Utiliser les valeurs actuelles du formulaire au lieu de answers$.value (Firebase)
    // answers$.value peut contenir des valeurs obsolètes car Firebase n'est pas mis à jour immédiatement
    // quand l'utilisateur change une réponse. Il faut utiliser form.value pour avoir les valeurs à jour.
    // getRawValue() inclut aussi les valeurs disabled, ce qui est important pour les règles
    const currentFormValues = this.activeSurvey$.value?.form?.getRawValue();
    
    if (currentFormValues) {
      // Mettre à jour answers$ avec les valeurs actuelles du formulaire pour garder la cohérence
      // et s'assurer que les règles sont évaluées avec les bonnes valeurs
      this.patchResponses(currentFormValues);
    } else {
      // Fallback si le formulaire n'est pas disponible
      this.patchResponses(this.answers$.value);
    }
  }

  private relogUserAfterSubmitSurvey(jwtToken: string) {
    this.authService.signInUsingToken(jwtToken)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Update la thunasse...
          this.rewardService.refreshRewardsUserInfo()
          this.publicationAccessService.evaluatePublicationRules(true)
          this.hasPendingRequest$.next(false)

          return this.publicationAccessService.pubUuid ?
            of(this.router.navigate(['/publication/display', this.publicationAccessService.pubUuid])) :
            of(this.router.navigate(['/home']))
        },
        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => event.addMetadata('Survey Viewer Service', {jwtToken}))
        }
      })
  }

  private buildDataPayload(prepareForPOST = false) {

    //
    // On tronque si la publication a pas le flag 'saveFromFirebase'
    //
    const shrink = this.publicationAccessService.publication$.value?.generalInfo?.moreOptions?.saveFromFirebase || false;
    if (!shrink) {
      prepareForPOST = false;
    }

    const formattedAnswersOfActiveQuestions = {}
    if (!prepareForPOST) {
      this.activeSurvey$.value.pages.forEach(p => {
        p.questions.forEach(q => {
          if (q.keepInSurveyAnswers()) {
            formattedAnswersOfActiveQuestions[q.key] = q.formatFormPayload(this.activeSurvey$.value.form)
          }
        })
      })

    }

    return {
      ...formattedAnswersOfActiveQuestions,
      version: '2022.0',
      timeStamp: new Date().toISOString(),
      pubUuid: this.publicationAccessService?.pubUuid || '[noPubUuid]',
      infos: {
        urlEntry: this.urlEntryService.getParams(),
        uniqSession: this.eventMessagesService?.uniqSession || 'noUniqSession?',
        originalUserId: this.userService.user?.id || 'noUserId?',
        browser: `${this.eventMessagesService?.browserName} (${this.eventMessagesService?.browserVersion})`,
      },
      elapsedDetail: {
        elapsedInSeconds: this.calculateTimeElapsedInSeconds()
      },
      userExtProfilesAtAnswerSubmit: this.userService.user$.value.extProfiles
    }

  }

  private buildShrinkDataPayload() {

    const formattedAnswersOfActiveQuestions = {}

    this.activeSurvey$.value.pages.forEach(p => {
      p.questions.forEach(q => {
        if (q.keepInSurveyAnswers()) {
          formattedAnswersOfActiveQuestions[q.key] = q.formatFormPayload(this.activeSurvey$.value.form)
        }
      })
    })

    return {
      ...formattedAnswersOfActiveQuestions,
      version: '2022.0',
      timeStamp: new Date().toISOString(),
      pubUuid: this.publicationAccessService?.pubUuid || '[noPubUuid]',
      infos: {
        urlEntry: this.urlEntryService.getParams(),
        uniqSession: this.eventMessagesService?.uniqSession || 'noUniqSession?',
        originalUserId: this.userService.user?.id || 'noUserId?',
        browser: `${this.eventMessagesService?.browserName} (${this.eventMessagesService?.browserVersion})`,
      },
      elapsedDetail: {
        elapsedInSeconds: this.calculateTimeElapsedInSeconds()
      },
      userExtProfilesAtAnswerSubmit: this.userService.user$.value.extProfiles
    }

  }

  private calculateTimeElapsedInSeconds(): number {
    const startTime = new Date(this.activeSurvey$.value.form.get(this.activeSurvey$.value.startTimeControlName).value).getTime()
    const endTime = new Date(this.activeSurvey$.value.form.get(this.activeSurvey$.value.endTimeControlName).value).getTime()
    return ((endTime - startTime) / 1000) + (this.alreadyElapsedTime || 0);
  }

  private notifySuccessForPreviewMode() {
    this.toastr.info(
      `Les réponses au sondage sont valides, mais n'ont pas été enregistrées, car vous êtes en mode de prévisualisation. Le contenu qui serait sauvegardé est visible en console.`,
      'Sauvegarde des réponses',
      {timeOut: 10000}
    )
    console.info('%c Réponses au sondage ⬇️ ', 'color: #00A4FF', this.buildSurveySubmitBodyPayload())
  }

  private async syncAnswersWithFirebase(): Promise<void | boolean> {

    if (this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION) {
      return false
    }

    const toSave = {
      timeStamp: new Date().toISOString(),
      datas: this.buildDataPayload(),
    }

    //
    // En mode test, on va aussi stocker les valeurs de haveRules au passage
    //
    if (this.appModeService.isTestSurvey$.value) {
      this.testSurveyService.updateValuesForTest(this.haveRules$.value, toSave);
    }
    //
    //
    //

    const objectPath = `temp/${this.activeSurvey$.value.id}/${this.userService.user.id}`
    return runInInjectionContext(this.injector, () =>
      this.db.object(objectPath).set(toSave)
        .then(() => runInInjectionContext(this.injector, () => true))
        .catch((error) => runInInjectionContext(this.injector, () => {
          console.error('[syncAnswersWithFirebase] ERROR', error);
          return true;
        }))
    )
  }

  private applyMaxAnswers() {
    this.activeSurvey$.value.getQuestionsReferencedInStructureElements().forEach(q => {
      q.applyMaxAnswers(this.activeSurvey$.value.form)
    })
  }

  private initializeViewer(rawSurvey: ISurvey) {

    this.flowFeedbackService.setInitializeViewer(true);
    this.initialRawSurvey$.next(rawSurvey);
    this.haveRules$.next({...rawSurvey.haveRules});

    this.allFormControlsMarkedAsTouched$.next(false)

    if (this.formSub$) {
      this.formSub$.unsubscribe()
    }

    this.buildSurvey(rawSurvey)
    if (this.activeSurvey$.value?.form) {
      this.setupFormListener()
    }
    this.flowFeedbackService.setInitializeViewer(false);

  }

  private setupFormListener() {
    if (!this.activeSurvey$.value?.form) {
      return
    }
    this.updatedElapsedTime()
    this.formSub$ = this.activeSurvey$.value.form.valueChanges
      .subscribe(async () => {
        this.updatedElapsedTime()
        await this.syncAnswersWithFirebase()
        // Réévaluer les règles avec les nouvelles valeurs du formulaire
        // Cela permet de mettre à jour l'affichage des questions/images quand les règles changent
        this.relaunchEvalRules()
        // Mettre à jour l'état des validations audio
        this.updatePendingAudioValidations()
      })
    
    // Surveiller les changements d'upload audio pour mettre à jour l'état
    this.updatePendingAudioValidations()
  }

  private updatePendingAudioValidations() {
    const pendingQuestions = this.getPendingAudioValidations()
    this.pendingAudioValidations$.next(pendingQuestions)
    // Mettre à jour aussi les questions invalides
    const invalidQuestions = this.getInvalidAudioQuestions()
    this.invalidAudioQuestions$.next(invalidQuestions)
  }

  private updatedElapsedTime() {
    const survey = this.activeSurvey$.value
    if (!survey?.form) {
      return
    }
    const now = new Date().toISOString()
    if (!survey.form.get(survey.startTimeControlName).value) {
      survey.form.get(survey.startTimeControlName).setValue(now, {emitEvent: false})
    }
    survey.form.get(survey.endTimeControlName).setValue(now, {emitEvent: false})
  }

  private buildSurvey(rawSurvey: ISurvey) {
    let survey


    //
    // Inject HonyPotQuestion
    //
    // En fait je vais toujours le mettre... on verra si il y a des réponses qui rentrent dans ça ;)
    //git
    rawSurvey = this.honeyPotService.injectHoneyPotQuestion(rawSurvey);

    const pub = this.publicationAccessService.publication$.value;

    //
    // Injecter les règles audio de la publication dans les questions audio du sondage
    //
    if (pub?.generalInfo?.audioQuestionRules && rawSurvey?.surveyDef?.questions) {
      const audioQuestionRules = pub.generalInfo.audioQuestionRules;
      
      // Convertir les objets (stockés par Firebase) en tableaux pour les règles
      const convertObjectsToArrays = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return obj;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(item => convertObjectsToArrays(item));
        }
        
        if (typeof obj === 'object') {
          const keys = Object.keys(obj);
          const allNumericKeys = keys.length > 0 && keys.every(key => /^\d+$/.test(key));
          
          if (allNumericKeys) {
            const sortedKeys = keys.map(k => parseInt(k, 10)).sort((a, b) => a - b);
            return sortedKeys.map(key => convertObjectsToArrays(obj[key.toString()]));
          }
          
          const converted: any = {};
          for (const [key, value] of Object.entries(obj)) {
            converted[key] = convertObjectsToArrays(value);
          }
          return converted;
        }
        
        return obj;
      };

      audioQuestionRules.forEach((audioRule: any) => {
        const questionKey = audioRule.questionKey;
        const question = rawSurvey.surveyDef.questions[questionKey];
        
        if (question && audioRule.rules) {
          // Appliquer les règles de la publication à la question du sondage
          if (!question.rules) {
            question.rules = {};
          }
          
          // Convertir et appliquer chaque type de règle
          ['stopIf', 'visibleIf', 'hideIf'].forEach(ruleType => {
            if (audioRule.rules[ruleType]) {
              const ruleData = audioRule.rules[ruleType];
              
              if (!question.rules[ruleType]) {
                question.rules[ruleType] = {};
              }
              
              // Convertir finalExpression d'objet en tableau si nécessaire
              if (ruleData.finalExpression) {
                question.rules[ruleType].finalExpression = convertObjectsToArrays(ruleData.finalExpression);
              }
              
              // Convertir filterText d'objet en tableau si nécessaire
              if (ruleData.filterText !== undefined) {
                question.rules[ruleType].filterText = convertObjectsToArrays(ruleData.filterText);
              }
              
              // Copier label si défini
              if (ruleData.label !== undefined && ruleData.label !== null) {
                question.rules[ruleType].label = ruleData.label;
              }
            }
          });
        }
      });
    }


    try {
      if (this.appModeService.appMode$.value === AppMode.PREVIEW){
        this.langsListParamsService.updateLangsList(rawSurvey.surveyDef.activeLang || ['fr', 'en'])
      }

      survey = new Survey(
        rawSurvey,
        this.userService,
        {
          ...defaultSurveyConstructorParams,
          singlePreviewMode: this.appModeService.appMode$.value === AppMode.SINGLE_QUESTION,
          previewMode: this.appModeService.appMode$.value === AppMode.PREVIEW,
          applyRules:
            !(this.appModeService.appPreviewMode$.value === AppPreviewMode.TABLE || !this.appModeService.applyRules$.value),
          aliasTitle: pub.generalInfo.surveysAlias.doAliasSurvey ? pub.generalInfo.surveysAlias.aliases[rawSurvey.id] : undefined,
        },
      )
      this.activeSurvey$.next(survey)
      this.activeSurvey$.value.form.patchValue(this.answers$.value, {emitEvent: false})
      
      // Mettre à jour l'état des validations audio après l'initialisation du sondage
      this.updatePendingAudioValidations()

    } catch (e) {
      console.error(e);
      Bugsnag.notify(e, (event) => {
        event.addMetadata('Survey Viewer Service', {
          survey: rawSurvey,
          errorMsg: e
        })
      })
      if (this.appModeService.appMode$.value !== AppMode.SINGLE_QUESTION) {
        this.toastr.error(e, this.translocoService.translate('Survey initialization failure'), {
          disableTimeOut: true
        })
      }
    }
  }

  isPreview(): boolean {
    return this.appModeService.appMode$.value === AppMode.PREVIEW;
  }

  private callValidateAnswerWithAI(submitBody: any, response: any): void {
    const endpoint = `${environment.apiUrl}/appCommunity/validateAnswerWithAI`;
    const body = {
      surveyId: submitBody.surveyId,
      userId: response.data.userDetails.userId,
    };

    console.log('[callValidateAnswerWithAI] Début validation AI:', {
      endpoint,
      body,
      surveyId: submitBody.surveyId,
      userId: response.data.userDetails.userId
    });

    console.log('[callValidateAnswerWithAI] Appel direct de la validation...');

    this.http.post(endpoint, body).subscribe({
      next: (validationResponse: any) => {
        console.log('[callValidateAnswerWithAI] Validation AI réussie:', validationResponse);
      },
      error: (err) => {
        console.error('[callValidateAnswerWithAI] Erreur validation AI:', err);
      }
    });
  }

  /**
   * Vérifie si toutes les questions audio sont validées complètement
   * Ne vérifie que les questions audio qui sont affichées à l'utilisateur et en cours de validation
   * @returns Array des clés de questions audio qui ne sont pas encore validées complètement
   */
  getPendingAudioValidations(): string[] {
    const pendingQuestions: string[] = []
    const survey = this.activeSurvey$.value

    if (!survey) {
      return pendingQuestions
    }

    // Créer un Set des clés de questions qui sont dans les pages actives (visibles)
    const visibleQuestionKeys = new Set<string>()
    survey.pages.forEach(page => {
      page.questions.forEach(q => {
        visibleQuestionKeys.add(q.key)
      })
    })

    // Parcourir toutes les questions du sondage
    Object.values(survey.questions).forEach((question: Question) => {
      // Vérifier si c'est une question audio
      if (question.viewerType === ViewerTypeEnum.AUDIORECORDER) {
        const questionKey = question.key
        
        // Ignorer les questions qui ne sont pas dans les pages actives (jamais affichées)
        if (!visibleQuestionKeys.has(questionKey)) {
          return // Question jamais affichée, on l'ignore
        }
        
        // Vérifier si la question est visible (pas cachée par les règles)
        const questionFormControl = survey.form.get(questionKey)
        if (!questionFormControl) {
          return // Question non présente dans le formulaire, on l'ignore
        }

        // Vérifier si l'audio a été uploadé et validé complètement
        const uploadedAudioSubject = this.audioUploadService.uploadedAudio$[questionKey]
        const uploadedAudio = uploadedAudioSubject?.value
        
        // Vérifier si un upload est en cours
        const isUploadingSubject = this.audioUploadService.isUploading$[questionKey]
        const isUploading = isUploadingSubject?.value === true
        
        // Vérifier si la question a été touchée/interagie (indique qu'elle a été affichée)
        const hasBeenTouched = questionFormControl.touched || questionFormControl.dirty
        
        // Vérifier si un upload a été initié (même si pas encore terminé)
        const hasUploadBeenInitiated = uploadedAudioSubject !== undefined || 
                                      isUploadingSubject !== undefined ||
                                      questionFormControl.value !== null ||
                                      hasBeenTouched
        
        // Ne vérifier que les questions qui ont été affichées ET qui sont en cours de validation
        // Une question est "en cours de validation" si :
        // - Un upload est en cours
        // - Un upload a été initié mais la transcription n'est pas encore valide
        if (!hasUploadBeenInitiated) {
          return // Question jamais affichée/interagie, on l'ignore
        }
        
        // Une question audio est validée complètement si :
        // 1. Elle a un uploadedAudio avec une transcription valide (longueur > 0)
        // 2. Le formControl est VALID et n'a pas la valeur temporaire 'uploading'
        // 3. Aucun upload n'est en cours
        const hasValidTranscription = uploadedAudio?.transcription?.length > 0
        const formControlValue = questionFormControl.value
        const isFormControlValid = questionFormControl.valid && 
                                   formControlValue !== 'uploading' && 
                                   formControlValue !== null && 
                                   formControlValue !== undefined
        
        // Si un upload est en cours, la question est en attente (même si optionnelle)
        if (isUploading) {
          pendingQuestions.push(questionKey)
          return
        }

        // Si la question est requise (non optionnelle) et n'est pas validée complètement
        if (!question.isOptional) {
          // Si pas de transcription valide mais qu'un upload a été initié, la question est en attente
          if (!hasValidTranscription && hasUploadBeenInitiated) {
            pendingQuestions.push(questionKey)
          }
          // Si le formControl n'est pas valide et qu'un upload a été initié, la question est en attente
          else if (!isFormControlValid && hasUploadBeenInitiated) {
            pendingQuestions.push(questionKey)
          }
        }
      }
    })

    return pendingQuestions
  }

  /**
   * Vérifie si toutes les questions audio sont validées complètement
   * @returns true si toutes les questions audio sont validées, false sinon
   */
  areAllAudioQuestionsValidated(): boolean {
    return this.getPendingAudioValidations().length === 0
  }

  /**
   * Récupère les questions audio qui sont vraiment invalides (pas juste en cours d'upload)
   * @returns Array des clés de questions audio invalides (exclut celles en cours d'upload)
   */
  getInvalidAudioQuestions(): string[] {
    const invalidQuestions: string[] = []
    const survey = this.activeSurvey$.value

    if (!survey) {
      return invalidQuestions
    }

    // Créer un Set des clés de questions qui sont dans les pages actives (visibles)
    const visibleQuestionKeys = new Set<string>()
    survey.pages.forEach(page => {
      page.questions.forEach(q => {
        visibleQuestionKeys.add(q.key)
      })
    })

    // Parcourir toutes les questions du sondage
    Object.values(survey.questions).forEach((question: Question) => {
      // Vérifier si c'est une question audio
      if (question.viewerType === ViewerTypeEnum.AUDIORECORDER) {
        const questionKey = question.key
        
        // Ignorer les questions qui ne sont pas dans les pages actives (jamais affichées)
        if (!visibleQuestionKeys.has(questionKey)) {
          return
        }
        
        const questionFormControl = survey.form.get(questionKey)
        if (!questionFormControl) {
          return
        }

        // Vérifier si un upload est en cours
        const isUploadingSubject = this.audioUploadService.isUploading$[questionKey]
        const isUploading = isUploadingSubject?.value === true
        
        // Si un upload est en cours, ce n'est pas une question invalide, juste en attente
        if (isUploading) {
          return
        }

        // Vérifier si la question a été touchée/interagie
        const hasBeenTouched = questionFormControl.touched || questionFormControl.dirty
        
        // Vérifier si un upload a été initié
        const uploadedAudioSubject = this.audioUploadService.uploadedAudio$[questionKey]
        const uploadedAudio = uploadedAudioSubject?.value
        const hasUploadBeenInitiated = uploadedAudioSubject !== undefined || 
                                      isUploadingSubject !== undefined ||
                                      questionFormControl.value !== null ||
                                      hasBeenTouched
        
        // Ne vérifier que les questions qui ont été affichées/interagies
        if (!hasUploadBeenInitiated) {
          return
        }
        
        // Une question est invalide si :
        // - Elle n'est pas en cours d'upload (déjà vérifié)
        // - Elle n'a pas de transcription valide OU le formControl n'est pas valide
        const hasValidTranscription = uploadedAudio?.transcription?.length > 0
        const formControlValue = questionFormControl.value
        const isFormControlValid = questionFormControl.valid && 
                                   formControlValue !== 'uploading' && 
                                   formControlValue !== null && 
                                   formControlValue !== undefined
        
        // Si la question est requise (non optionnelle) et est invalide
        if (!question.isOptional) {
          if (!hasValidTranscription || !isFormControlValid) {
            invalidQuestions.push(questionKey)
          }
        }
      }
    })

    return invalidQuestions
  }

}
